<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import moment from "moment-timezone";

import { capitalizeFirstLetter } from "@/helpers/string";
import { useConfigurationStore } from "@/store/configurationStore";

import type { FormattedProfile, ProfilPreviewFormProps } from "@/components/modal/Profile/previewProfile/types";
import type { ICountry, INationality } from '@/types/geo';

import DatePicker from "@/dsComponents/DatePicker/DatePicker.vue";
import AddressAutocomplete from "@/dsComponents/inputs/AddressAutocomplete.vue";
import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";

import ProfilPreviewFormItem from "@/components/modal/Profile/previewProfile/ProfilPreviewFormItem.vue";

const props = defineProps<ProfilPreviewFormProps>();

const { t } = useI18n();

const configStore = useConfigurationStore();

const emit = defineEmits<{
    (e: "update:field", field: keyof FormattedProfile, value: FormattedProfile[keyof FormattedProfile]): void;
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    (e: "update:address", value: any): void;
}>();

const address = ref(props.address);

watch(
    () => props.address,
    (newValue) => {
        address.value = newValue;
    }
);

const nationalities = computed(() => {
    const nats = configStore.nationalitiesOptions?.filter((nat: INationality) => nat.code !== "");
    return nats?.map((nat: INationality) => ({ label: capitalizeFirstLetter(nat.name), value: nat.code }));
});

const nationalityLabel = computed(() => {
    const code = props.profile?.nationality;
    if (!code) return "";
    const found = nationalities.value?.find((nat) => nat.value === code);
    return found?.label ?? code;
});

    const countries = computed(() => {
        return configStore.countriesOptions?.map((country: ICountry) => ({
            label: capitalizeFirstLetter(country.name),
            value: country.code,
        }));
    });

const birthCountryLabel = computed(() => {
    const code = props.profile?.birthCountry;
    if (!code) return "";
    const found = countries.value?.find((country) => country.value === code);
    return found?.label ?? code;
});

const maxBirthDate = computed(() => moment().subtract(16, "years").toDate());
</script>

<template>
    <div class="profil-preview-form">
        <div class="profil-preview-form__form">
            <ProfilPreviewFormItem :label="t('form.lastname')" icon="doc-user-id">
                <TextInputAlt
                    :modelValue="profile?.lastname ?? ''"
                    variant="grey"
                    data-bot="lastname"
                    @update:modelValue="emit('update:field', 'lastname', $event)"
                />
            </ProfilPreviewFormItem>

            <ProfilPreviewFormItem :label="t('form.firstname')" icon="doc-user-id">
                <TextInputAlt
                    :modelValue="profile?.firstname ?? ''"
                    variant="grey"
                    data-bot="firstname"
                    @update:modelValue="emit('update:field', 'firstname', $event)"
                />
            </ProfilPreviewFormItem>

            <ProfilPreviewFormItem :label="t('form.email')" icon="at">
                <TextInputAlt
                    :modelValue="profile?.email ?? ''"
                    variant="grey"
                    data-bot="email"
                    @update:modelValue="emit('update:field', 'email', $event)"
                />
            </ProfilPreviewFormItem>

            <ProfilPreviewFormItem :label="t('form.phone')" icon="phone">
                <TextInputAlt
                    :modelValue="profile?.phone ?? ''"
                    :placeholder="t('global.notProvided')"
                    variant="grey"
                    type="tel"
                    data-bot="phone"
                    @update:modelValue="emit('update:field', 'phone', $event)"
                />
            </ProfilPreviewFormItem>

            <ProfilPreviewFormItem :label="t('modals.profilPreview.form.birthDate')" icon="cal">
                <DatePicker
                    :value="profile?.birthDate ?? ''"
                    :max-date="maxBirthDate"
                    :start-date="maxBirthDate"
                    :placeholder="t('global.notProvided')"
                    variant="ghost"
                    format="DD/MM/YYYY"
                    data-bot="birthDate"
                    class="profil-preview-form__form-item-date-picker"
                    @datePicked="emit('update:field', 'birthDate', $event)"
                />
            </ProfilPreviewFormItem>

            <ProfilPreviewFormItem :label="t('form.address')" icon="map-pin">
                <AddressAutocomplete
                    :label="t('form.address')"
                    v-model="address"
                    :placeholder="t('global.notProvided')"
                    iconLeft=""
                    width="247px"
                    variant="grey"
                    size="xs"
                    alt
                    data-bot="address"
                    @address="emit('update:address', $event)"
                />
            </ProfilPreviewFormItem>

            <ProfilPreviewFormItem :label="t('form.nationality')" icon="world">
                <TextInputAlt :model-value="nationalityLabel" :placeholder="t('global.notProvided')" readonly variant="grey" data-bot="nationality" />
            </ProfilPreviewFormItem>

            <ProfilPreviewFormItem :label="t('modals.profilPreview.form.birthPlace')" icon="world-pin">
                <TextInputAlt
                    :model-value="profile?.birthPlace ?? ''"
                    :placeholder="t('global.notProvided')"
                    readonly
                    variant="grey"
                    data-bot="birthPlace"
                />
            </ProfilPreviewFormItem>

            <ProfilPreviewFormItem :label="t('modals.profilPreview.form.birthCountry')" icon="map-pin-circle">
                <TextInputAlt
                    :model-value="birthCountryLabel"
                    :placeholder="t('global.notProvided')"
                    readonly
                    variant="grey"
                    data-bot="birthCountry"
                />
            </ProfilPreviewFormItem>

            <ProfilPreviewFormItem :label="t('modals.profilPreview.form.nir')" icon="doc-user-id">
                <TextInputAlt
                    :model-value="profile?.nir ?? ''"
                    :placeholder="t('global.notProvided')"
                    readonly
                    variant="grey"
                    data-bot="nir"
                />
            </ProfilPreviewFormItem>
        </div>
    </div>
</template>

<style scoped lang="scss">
.profil-preview-form {
    &__form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md, 8px);
        padding-left: var(--spacing-lg, 12px);

        &-item {
            display: flex;
            align-items: center;

            &-label {
                display: flex;
                align-items: center;
                flex-shrink: 0;
                gap: var(--spacing-sm, 6px);
                width: 124px;
                color: var(--text-sub-600, #494e61);

                &-icon {
                    color: var(--icon-sub-500, #636778);
                }
            }

            &-date-picker {
                width: 100%;

                :deep(.ds-combo-button) {
                    width: 100%;
                }
            }
        }
    }
}
</style>
